import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LOGO_PATH, BUSINESS_NAME } from '../../config/brand';
import { LayoutDashboard, PawPrint, CalendarPlus, CalendarCheck, FileText, Bell, Settings, LogOut, Scissors } from 'lucide-react';

const navItems = [
  { path: '/client/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/client/pets', label: 'My Pets', icon: PawPrint },
  { path: '/client/book', label: 'Book', icon: CalendarPlus },
  { path: '/client/appointments', label: 'Appointments', icon: CalendarCheck },
  { path: '/client/invoices', label: 'Invoices', icon: FileText },
  { path: '/client/notifications', label: 'Alerts', icon: Bell },
];

export default function ClientLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Top Navbar (desktop) */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E0] hidden md:block">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/client/dashboard')}>
              <img src={LOGO_PATH} alt={BUSINESS_NAME} className="h-9 object-contain" />
            </div>
            <div className="flex items-center gap-1 ml-6">
              {navItems.map((item) => {
                const active = location.pathname.startsWith(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${active ? 'bg-[#6A3E28] text-white' : 'text-[#5A5A5A] hover:bg-[#F5EDE6]'}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#E2E8E4] rounded-full flex items-center justify-center text-sm font-semibold text-[#2D2D2D]">
                {user?.name?.[0] || 'U'}
              </div>
              <span className="text-sm font-medium text-[#2D2D2D]">{user?.name}</span>
            </div>
            <button onClick={handleLogout} className="text-[#888] hover:text-[#2D2D2D]" data-testid="client-logout-btn">
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 pb-24 md:pb-6">
        <Outlet />
      </main>

      {/* Mobile Bottom Nav */}
      <div className="mobile-nav md:hidden">
        {navItems.slice(0, 5).map((item) => {
          const active = location.pathname.startsWith(item.path);
          const Icon = item.icon;
          return (
            <button key={item.path} onClick={() => navigate(item.path)} className={`mobile-nav-item ${active ? 'active' : ''}`} data-testid={`mobile-nav-${item.label.toLowerCase()}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
